/**
 * Revisions Dual-Write Test
 *
 * Tests that revision operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * R1: Create single item revision
 * R2: Create multiple items revision
 * R3: Delete revision
 */

import {
  saveNewRevision,
  deleteRevision,
} from '../global/Firebase/inventory';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, productsDictAtom, dbAtom } from '../global/AtomsProvider';

// Test variation definitions
export const REVISION_TEST_VARIATIONS = [
  { id: 'R1', category: 'create', name: 'Create single item revision' },
  { id: 'R2', category: 'create', name: 'Create multiple items revision' },
  { id: 'R3', category: 'delete', name: 'Delete revision' },
];

// Get test categories for UI grouping
export function getRevisionTestCategories() {
  return [
    { id: 'create', name: 'Create Revisions', variations: ['R1', 'R2'] },
    { id: 'delete', name: 'Delete Revision', variations: ['R3'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getRevisionTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const db = jotaiStore.get(dbAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  // Get first few products for revision items
  const productEntries = Object.entries(productsDict || {});
  const testProducts = productEntries.slice(0, 3).map(([id, product]) => ({
    id,
    ...product,
    ref: db.collection('products').doc(id),
  }));

  return {
    organization,
    store,
    db,
    organizationId: organization.id,
    testProducts,
  };
}

/**
 * Compare revision via backend API
 */
async function compareRevisionViaApi(revisionId, revisionData, log) {
  try {
    const response = await api.get(`/revisions/compare/${revisionId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        revisionId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'revision',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    const fieldsToCompare = [
      { fs: 'amount', sb: 'amount' },
    ];

    for (const { fs, sb } of fieldsToCompare) {
      const fsVal = normalizeValue(revisionData?.[fs]);
      const sbVal = normalizeValue(supabaseData?.[sb]);

      if (!valuesMatch(fsVal, sbVal)) {
        differences.push({
          field: sb,
          supabaseValue: sbVal,
          firestoreValue: fsVal,
          severity: 'critical',
        });
      }
    }

    // Compare items count
    const fsItemsCount = revisionData?.items?.length || 0;
    const sbItemsCount = supabaseData?.items?.length || 0;
    if (fsItemsCount !== sbItemsCount) {
      differences.push({
        field: 'items_count',
        supabaseValue: sbItemsCount,
        firestoreValue: fsItemsCount,
        severity: 'warning',
      });
    }

    return {
      revisionId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      revisionId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if revision was deleted from Supabase
 */
async function checkRevisionDeleted(revisionId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/revisions/compare/${revisionId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single revision test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, organizationId, testProducts } = testData;

  let createdRef = null;
  let createdRevisionData = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'R1': {
        // Create single item revision
        const timestamp = Date.now();
        const testProduct = testProducts[0];

        if (!testProduct) {
          result.error = 'No test product available';
          break;
        }

        const currentStock = testProduct.storeStock?.[store.id] || 0;
        const newStock = currentStock + 5; // Add 5 units

        const items = [{
          id: testProduct.id,
          name: testProduct.name || 'Test Product',
          inStockOld: currentStock,
          inStock: newStock,
          purchasePrice: testProduct.purchasePrice || 100,
          ref: testProduct.ref,
        }];

        const amount = items.reduce((sum, item) =>
          sum + (item.purchasePrice || 0) * (item.inStock - item.inStockOld), 0);
        const correctedAmount = amount;
        const positiveAmount = amount > 0 ? amount : 0;
        const negativeAmount = amount < 0 ? Math.abs(amount) : 0;

        const revisionData = {
          organizationId,
          store: store.id,
          items,
          amount,
          correctedAmount,
          positiveAmount,
          negativeAmount,
          date: new Date(),
          dailyStatID: `test-revision-${timestamp}-${store.id}`,
        };

        log(`Creating single item revision: ${testProduct.name}, stock ${currentStock} -> ${newStock}`);
        createdRef = await saveNewRevision(db, revisionData);
        createdRevisionData = { ...revisionData, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareRevisionViaApi(createdRef.id, revisionData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test revision...');
        await deleteRevision(db, createdRevisionData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'R2': {
        // Create multiple items revision
        const timestamp = Date.now();

        if (testProducts.length < 2) {
          result.error = 'Need at least 2 products for multiple items test';
          break;
        }

        const items = testProducts.slice(0, 2).map((product, index) => {
          const currentStock = product.storeStock?.[store.id] || 0;
          const newStock = currentStock + (3 + index);
          return {
            id: product.id,
            name: product.name || `Test Product ${index + 1}`,
            inStockOld: currentStock,
            inStock: newStock,
            purchasePrice: product.purchasePrice || 100 + index * 50,
            ref: product.ref,
          };
        });

        const amount = items.reduce((sum, item) =>
          sum + (item.purchasePrice || 0) * (item.inStock - item.inStockOld), 0);
        const correctedAmount = amount;
        const positiveAmount = amount > 0 ? amount : 0;
        const negativeAmount = amount < 0 ? Math.abs(amount) : 0;

        const revisionData = {
          organizationId,
          store: store.id,
          items,
          amount,
          correctedAmount,
          positiveAmount,
          negativeAmount,
          date: new Date(),
          dailyStatID: `test-revision-multi-${timestamp}-${store.id}`,
        };

        log(`Creating multi-item revision: ${items.length} items, total amount: ${amount}`);
        createdRef = await saveNewRevision(db, revisionData);
        createdRevisionData = { ...revisionData, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareRevisionViaApi(createdRef.id, revisionData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test revision...');
        await deleteRevision(db, createdRevisionData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'R3': {
        // Delete revision test
        const timestamp = Date.now();
        const testProduct = testProducts[0];

        if (!testProduct) {
          result.error = 'No test product available';
          break;
        }

        const currentStock = testProduct.storeStock?.[store.id] || 0;
        const newStock = currentStock + 1;

        const items = [{
          id: testProduct.id,
          name: testProduct.name || 'Test Product',
          inStockOld: currentStock,
          inStock: newStock,
          purchasePrice: testProduct.purchasePrice || 50,
          ref: testProduct.ref,
        }];

        const amount = (items[0].purchasePrice || 0) * (newStock - currentStock);

        const revisionData = {
          organizationId,
          store: store.id,
          items,
          amount,
          correctedAmount: amount,
          positiveAmount: amount > 0 ? amount : 0,
          negativeAmount: amount < 0 ? Math.abs(amount) : 0,
          date: new Date(),
          dailyStatID: `test-revision-del-${timestamp}-${store.id}`,
        };

        log('Creating revision to delete...');
        createdRef = await saveNewRevision(db, revisionData);
        createdRevisionData = { ...revisionData, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareRevisionViaApi(createdRef.id, revisionData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Revision was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting revision...');
        await deleteRevision(db, createdRevisionData);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkRevisionDeleted(createdRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          revisionId: createdRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdRevisionData) {
      try {
        await deleteRevision(db, createdRevisionData);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all revision tests
 */
export async function runFullRevisionTest(log = console.log, selectedVariations = null) {
  log('Starting revision dual-write tests...');

  const testData = getRevisionTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store and to be logged in.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  if (testData.testProducts.length < 1) {
    log('ERROR: Need at least 1 product to run revision tests.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('REVISIONS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log(`Test Products: ${testData.testProducts.map(p => p.name || p.id).join(', ')}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || REVISION_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = REVISION_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = REVISION_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  REVISION_TEST_VARIATIONS,
  getRevisionTestCategories,
  getRevisionTestData,
  runFullRevisionTest,
};
